import React, { useEffect ,useState,useCallback} from 'react';
import { Container } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import milkImage from '../../../Assets/Image/Attraction/cultural/food.jpg';
import cuisineImage from '../../../Assets/Image/Attraction/cultural/food2.jpg';
import mufeImage from '../../../Assets/Image/Attraction/cultural/food3.jpg';
import mealsImage from '../../../Assets/Image/Attraction/cultural/milk.jpg';
import bannerImage from '../../../Assets/Image/Attraction/cultural/craft.jpg';

const CulturalPage = () => {
  const controls = useAnimation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const attractions = [
    {
      id: 1,
      title: "Afar Milk: A Symbol of Hospitality",
      content: "Milk holds a deep cultural significance among the Afar people. In village communities, it is not just a source of nourishment but an essential social offering. Camel milk, in particular, is a staple, commonly found in traditional markets and enjoyed in most Afar villages.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 2,
      title: "Traditional Afar Cuisine",
      content: "The Afar people have unique culinary traditions rooted in their way of life. Despite the lack of modern ovens, they have created innovative cooking techniques that add authenticity to their cuisine.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 3,
      title: "Mufe: The Afar Oven",
      content: "The traditional mufe functions like a complement to a modern microwave oven. It is a hot metal box buried under the ground and heated from within and without, allowing the food to bake evenly with just the right amount of heat.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 4,
      title: "Types of Afar Meals",
      content: "Traditional meals include Mufie/Geomo (prepared from maize powder), Sabita (made from wheat or maize), and Qualima and Tabeno (traditional meat dishes).",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 5,
      title: "Cultural Dressing of Afar",
      content: "The traditional attire reflects their identity and pride. Women wear the Ubukie and shash, while men don the Sanafil, Mesenef, and Ryeto.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 6,
      title: "Ornaments and Jewelry",
      content: "Afar people wear a variety of cultural ornaments, including rings, bracelets, anklets, and waist jewelry, each with its own symbolic meaning.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 7,
      title: "Cultural Marriage Traditions",
      content: "The Afar community practices two main types of traditional marriages: Absuma (customary marriage between relatives) and Konfera (marriage based on mutual agreement).",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 8,
      title: "Leadership Legacy: Alimirah Hanfare",
      content: "Alimirah Hanfare was the 15th spiritual and tribal leader of the Afar from 1944 to 2003. His legacy is commemorated by his castles, located east of Asaita near the Awash River.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 9,
      title: "Afar Hairstyles",
      content: "Traditional hairstyles include Dere Kamo, Aydi, Ssedogo, Da'ita, and Besita, each symbolizing different life stages and marital status.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 10,
      title: "Traditional Afar Dance",
      content: "Traditional dances include Hora, Sedea, Tiretera, Kekle, and Melabo, each serving different cultural purposes and occasions.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 11,
      title: "Afar Village Life",
      content: "The Afar people are pastoralists and transhumant, moving between highlands and lowlands with the seasons and river floods.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    },
    {
      id: 12,
      title: "The Ari: A Portable Masterpiece",
      content: "The Ari is a portable house built by Afar women using palm fronds and matting, designed for easy dismantling and reassembly.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'right',
    },
    {
      id: 13,
      title: "Dagu: The Afar Way of Communication",
      content: "Dagu is an intricate system of oral information exchange, forming the backbone of Afar social interactions and community cohesion.",
      images: [milkImage, cuisineImage, mufeImage, mealsImage],
      position: 'left',
    }
  ];

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const handleImageClick = useCallback((image, title) => {
    setSelectedImage({ url: image, title });
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
  }, []);
  

  return (
    <StyledCulturalPage ref={ref}>
    <HeroBanner 
      role="banner" 
      aria-label="Cultural Heritage of Afar Banner"
      style={{ backgroundImage: `url(${bannerImage})` }}
    >
      <motion.div 
        className="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <h1>Cultural Heritage of Afar</h1>
        <p>Discover the Rich Traditions and Heritage of the Afar People</p>
      </motion.div>
    </HeroBanner>

    <Container>
      <motion.div
        initial="hidden"
        animate={controls}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.2
            }
          }
        }}
      >
        <IntroCard
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }
          }}
          whileHover={{ y: -5 }}
        >
          <p>
            The Afar people, indigenous to the African Horn, boast a distinct cultural and linguistic identity. 
            Their homeland spans the northeastern part of Ethiopia, spilling into Eritrea and Djibouti, 
            in what is commonly referred to as the Afar Triangle.
          </p>
        </IntroCard>

        <AttractionGrid role="list">
          {attractions.map((attraction) => (
            <AttractionCard
              key={attraction.id}
              variants={{
                hidden: { opacity: 0, y: 50 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    ease: "easeOut"
                  }
                }
              }}
              whileHover={{ y: -5 }}
              role="listitem"
            >
              <CardContent>
                <TextContent>
                  <h2>{attraction.title}</h2>
                  <p>{attraction.content}</p>
                </TextContent>
                <ImageWrapper>
                  <FeaturedImage>
                    <button
                      onClick={() => handleImageClick(attraction.images[0], attraction.title)}
                      aria-label={`View large image of ${attraction.title}`}
                    >
                      <img 
                        src={attraction.images[0]} 
                        alt={attraction.title}
                        loading="lazy"
                      />
                      <ImageOverlay className="image-overlay">
                        <span>View Details</span>
                      </ImageOverlay>
                    </button>
                  </FeaturedImage>
                  <ThumbnailGrid>
                    {attraction.images.slice(1).map((image, imgIndex) => (
                      <ThumbnailImage key={imgIndex}>
                        <button
                          onClick={() => handleImageClick(image, `${attraction.title} - ${imgIndex + 1}`)}
                          aria-label={`View thumbnail ${imgIndex + 1} of ${attraction.title}`}
                        >
                          <img 
                            src={image} 
                            alt={`${attraction.title} - ${imgIndex + 1}`}
                            loading="lazy"
                          />
                          <ImageOverlay className="image-overlay" />
                        </button>
                      </ThumbnailImage>
                    ))}
                  </ThumbnailGrid>
                </ImageWrapper>
              </CardContent>
            </AttractionCard>
          ))}
        </AttractionGrid>
      </motion.div>
    </Container>

    {selectedImage && (
      <Modal onClick={closeModal}>
        <ModalContent onClick={e => e.stopPropagation()}>
          <img src={selectedImage.url} alt={selectedImage.title} />
          <ModalClose onClick={closeModal} aria-label="Close modal">×</ModalClose>
          <ModalCaption>{selectedImage.title}</ModalCaption>
        </ModalContent>
      </Modal>
    )}
  </StyledCulturalPage>
  );
};

const StyledCulturalPage = styled.div`
  background: linear-gradient(to bottom, #f8f9fa, #ffffff);
`;

const HeroBanner = styled.div`
  height: 80vh;
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.5));
  }

  .hero-content {
    position: relative;
    z-index: 2;
    max-width: 800px;
    padding: 0 20px;

    h1 {
      font-size: 4rem;
      font-weight: 700;
      margin-bottom: 20px;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    }

    p {
      font-size: 1.5rem;
      text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
    }
  }
`;

const IntroCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  margin: -60px 20px 60px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: #4a5568;
    margin: 0;
  }
`;


const TextContent = styled.div`
  flex: 1;
  
  h2 {
    font-size: 2.5rem;
    color: #2d3748;
    margin-bottom: 20px;
    background: linear-gradient(45deg, #2d7ba8, #34495e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #4a5568;
  }
`;


const AttractionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  padding: 2rem 0;
`;
const AttractionCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease;
`;
const CardContent = styled.div`
  padding: 2rem;
`;
const ImageWrapper = styled.div`
  margin-top: 1.5rem;
`;
const FeaturedImage = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.33, 1, 0.68, 1);
  }

  &:hover {
    img {
      transform: scale(1.05);
    }

    .image-overlay {
      opacity: 1;
    }
  }
`;
const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ThumbnailImage = styled.div`
  position: relative;
  padding-top: 75%;
  border-radius: 12px;
  overflow: hidden;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
  }

  &:hover {
    img {
      transform: scale(1.05);
    }

    .image-overlay {
      opacity: 1;
    }
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2),
    rgba(0, 0, 0, 0.6)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  font-weight: 500;
  letter-spacing: 0.5px;
  backdrop-filter: blur(2px);

  span {
    padding: 0.75rem 1.5rem;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 30px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(5px);
  }
`;
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;

  img {
    max-width: 100%;
    max-height: 85vh;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: -40px;
  right: -40px;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 10px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ModalCaption = styled.p`
  color: white;
  text-align: center;
  margin-top: 1rem;
  font-size: 1.1rem;
`;
export default CulturalPage;